import { UAE, LEADERSHIP, EXPERTISE } from "./mock";

export const SITE_URL = process.env.REACT_APP_SITE_URL;
export const SITE_NAME = "Medium Plenty × UAE";
export const OG_IMAGE = `${SITE_URL}/mp/og-image.jpg`;

export const PAGES = {
  home: {
    path: "/",
    title: "Medium Plenty × UAE — Workplace Architecture & Interior Design, Abu Dhabi",
    description:
      "Medium Plenty is a California architecture and interior design studio with an Abu Dhabi office, delivering workplace, corporate headquarters, and tenant-improvement design and construction management across the UAE.",
  },
  work: {
    path: "/work",
    title: "Work — Medium Plenty × UAE",
    description:
      "Selected architecture and interior work by Medium Plenty — workplace, hospitality, and residential projects across the U.S. and the UAE, including the Global Headquarters in Abu Dhabi.",
  },
  studio: {
    path: "/studio",
    title: "Studio — Medium Plenty × UAE",
    description:
      "Meet the Medium Plenty team and learn about our architecture, interior design, and construction management services for workplaces in Abu Dhabi and the UAE.",
  },
  inquiries: {
    path: "/inquiries",
    title: "Inquiries — Medium Plenty × UAE",
    description:
      "Start a conversation with Medium Plenty about your workplace, hospitality, or residential project in Abu Dhabi, the UAE, or California.",
  },
};

const ORG_ID = `${SITE_URL}/#organization`;

const organization = {
  "@type": ["Organization", "ArchitecturalService"],
  "@id": ORG_ID,
  name: "Medium Plenty",
  alternateName: "Medium Plenty × UAE",
  url: SITE_URL,
  logo: `${SITE_URL}/mp/og-image.jpg`,
  image: OG_IMAGE,
  description: PAGES.home.description,
  email: "inquiries@mediumplenty.com",
  sameAs: ["https://www.mediumplenty.com", "https://www.mediumplenty.com/uae"],
  areaServed: [
    { "@type": "Country", name: "United Arab Emirates" },
    { "@type": "City", name: "Abu Dhabi" },
    { "@type": "City", name: "Dubai" },
    { "@type": "State", name: "California" },
  ],
  knowsAbout: EXPERTISE,
  location: [
    {
      "@type": "Place",
      name: "Medium Plenty — Abu Dhabi",
      address: { "@type": "PostalAddress", addressLocality: "Al Reem Island, Abu Dhabi", addressCountry: "AE" },
    },
    {
      "@type": "Place",
      name: "Medium Plenty — Northern California",
      address: { "@type": "PostalAddress", addressRegion: "CA", addressCountry: "US" },
    },
  ],
  founder: LEADERSHIP.filter((p) => p.role === "Founding Principal").map((p) => ({
    "@type": "Person",
    name: p.name,
    jobTitle: p.role,
  })),
  hasOfferCatalog: {
    "@type": "OfferCatalog",
    name: "Services",
    itemListElement: UAE.offerings.map((o) => ({
      "@type": "Offer",
      itemOffered: { "@type": "Service", name: o.title, description: o.body, provider: { "@id": ORG_ID } },
    })),
  },
};

const website = {
  "@type": "WebSite",
  "@id": `${SITE_URL}/#website`,
  url: SITE_URL,
  name: SITE_NAME,
  publisher: { "@id": ORG_ID },
  inLanguage: "en",
};

const webPage = (page) => ({
  "@type": "WebPage",
  "@id": `${SITE_URL}${page.path}#webpage`,
  url: `${SITE_URL}${page.path}`,
  name: page.title,
  description: page.description,
  isPartOf: { "@id": `${SITE_URL}/#website` },
  about: { "@id": ORG_ID },
  primaryImageOfPage: { "@type": "ImageObject", url: OG_IMAGE },
});

const featuredProject = {
  "@type": "CreativeWork",
  name: `${UAE.featured.title} — ${UAE.featured.location}`,
  description: UAE.featured.blurb,
  creator: { "@id": ORG_ID },
  locationCreated: { "@type": "Place", name: "Abu Dhabi, United Arab Emirates" },
  image: `${SITE_URL}${UAE.featured.hero}`,
  genre: "Workplace interior design",
};

const team = {
  "@type": "ItemList",
  name: "Medium Plenty Leadership",
  itemListElement: LEADERSHIP.map((p, i) => ({
    "@type": "ListItem",
    position: i + 1,
    item: {
      "@type": "Person",
      name: p.name,
      jobTitle: p.role,
      description: p.bio,
      worksFor: { "@id": ORG_ID },
    },
  })),
};

const contactPage = {
  "@type": "ContactPage",
  "@id": `${SITE_URL}/inquiries#contact`,
  url: `${SITE_URL}/inquiries`,
  name: "Inquiries",
  about: { "@id": ORG_ID },
};

const PAGE_GRAPH = {
  home: [featuredProject],
  work: [featuredProject],
  studio: [team],
  inquiries: [contactPage],
};

export const buildJsonLd = (key) => ({
  "@context": "https://schema.org",
  "@graph": [organization, website, webPage(PAGES[key]), ...(PAGE_GRAPH[key] || [])],
});
